import {
  AddressType,
  AddressV2Type,
  NaturalPersonAddressType,
  PersonAddressType,
} from 'types/address.type'
import { DocumentType } from 'types/document.type'
import { PhoneType } from 'types/phone.type'
import { toBR } from 'utils/date.util'
import { HearingStatusEnumType, hearingStatusOptions } from 'types/hearing.type'
import { validateField } from './validate.util'
import { CELLPHONE_MASK_REGEX, ONLY_NUMBER_REGEX, TELEPHONE_MASK_REGEX } from './regex.util'
import { cepMask } from 'utils/cep.util'

const PHONE_NUMBER_FORMAT = '($1) $2-'

export const formatText = (text?: string | number | null | undefined) => (text ? String(text) : '')

export const formatInformedQuantity = (data?: string | number | null): string => {
  if (typeof data === 'number') {
    return data.toString()
  }

  return formatText(data)
}

export const formatNaturalness = (city?: string | null, state?: string | null) => {
  const data = [city && `${city}`, state && `${state}`]
  if (city && city !== '' && state && state !== '') {
    return data.join(' / ')
  }
  return formatText(undefined)
}

export const formatDocument = ({
  descricaoIdTipoDocumento,
  numeroDocumento,
}: DocumentType): string => {
  if (descricaoIdTipoDocumento && numeroDocumento) {
    return `${descricaoIdTipoDocumento} - ${numeroDocumento}`
  }
  return formatText(undefined)
}

export const formatDocuments = (documents?: DocumentType[] | null): string[] => {
  if (documents) {
    return documents.map((document) => formatDocument(document))
  }
  return [formatText(undefined)]
}

export const formatAddressField = (item?: AddressType | null): string => {
  if (item) {
    const addressParts = [
      item.logradouro && `${item.logradouro}`,
      item.numero && `, ${item.numero}`,
      item.complemento && ` - ${item.complemento}`,
      item.bairroId && ` - ${item.bairro}`,
      item.ufId && `, ${item.uf}`,
      item.cidadeId && `, ${item.cidade}`,
      item.cep && ` - ${item.cep}`,
    ]

    return addressParts.filter(Boolean).join('')
  }

  return ''
}

export const formatAddressFieldV2 = (item?: AddressV2Type | null): string => {
  if (item) {
    const addressParts = [
      item.logradouro && `${item.logradouro}`,
      item.numero && `, ${item.numero}`,
      item.complemento && ` - ${item.complemento}`,
      item.bairro && ` - ${item.bairro.nome}`,
      item.cidade.uf && `, ${item.cidade.uf.nome}`,
      item.cidade && `, ${item.cidade.nome}`,
      item.cep && ` - ${item.cep}`,
    ]

    return addressParts.filter(Boolean).join('')
  }

  return ''
}

export const formatAddressFieldPerson = (item?: PersonAddressType | null): string => {
  if (item) {
    const addressParts = [
      item.endereco.logradouro && `${item.endereco.logradouro}`,
      item.endereco.numero && `, ${item.endereco.numero}`,
      item.endereco.complemento && ` - ${item.endereco.complemento}`,
      item.endereco.bairroId && ` - ${item.endereco.bairro}`,
      item.endereco.ufId && `, ${item.endereco.uf}`,
      item.endereco.cidadeId && `, ${item.endereco.cidade}`,
      item.endereco.cep && ` - ${cepMask(item.endereco.cep)}`,
    ]

    return addressParts.filter(Boolean).join('')
  }

  return ''
}

export const formatBirthdayEN = (data?: string | null) => {
  if (data) {
    return formatText(toBR(data))
  }
  return formatText(undefined)
}

export const formatAddresses = (addresses?: PersonAddressType[] | null): string[] => {
  if (addresses && addresses.length > 0) {
    return addresses.map((item) => formatAddressField(item.endereco))
  }
  return [formatText(undefined)]
}

export const formatAddressesV2 = (addresses?: NaturalPersonAddressType[] | null): string[] => {
  if (addresses && addresses.length > 0) {
    return addresses.map((item) => formatAddressFieldV2(item.endereco))
  }
  return [formatText(undefined)]
}

export const formatPhones = (phones?: PhoneType[] | null): string[] => {
  if (phones) {
    return phones.map(
      ({ telefone, descricao }) => `${telefone} ${descricao ? ` - ${descricao}` : ''}`,
    )
  }
  return [formatText(undefined)]
}

export const formatHearingStatus = (status?: HearingStatusEnumType) => {
  if (status) {
    const labelStatus = hearingStatusOptions.find((item) => item.value === status)
    return formatText(labelStatus?.label)
  }
  return formatText(undefined)
}

export const numberToText = (value?: number) => {
  if (value) {
    return value.toString()
  }
  return undefined
}

export const booleanSimNao = (text?: string | null): boolean | null => {
  if (text === null) return null
  switch (text) {
    case 'E':
      return true
    case 'S':
      return true
    default:
      return false
  }
}

export const formatChoiceText = (text?: string | null): string => {
  switch (text) {
    case 'E':
      return 'Eventualmente'
    case 'S':
      return 'Sim'
    case 'F':
      return 'Falecido'
    default:
      return 'Não'
  }
}

export const formatZipCode = (code: string): string => {
  const validatedCode = validateField(code)
  return validatedCode === '' ? validatedCode : code.replace(ONLY_NUMBER_REGEX, '')
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  const formattedNumber = phoneNumber.replace(ONLY_NUMBER_REGEX, '')
  if (formattedNumber.length === 10) {
    return formattedNumber.replace(TELEPHONE_MASK_REGEX, PHONE_NUMBER_FORMAT)
  } else if (formattedNumber.length === 11) {
    return formattedNumber.replace(CELLPHONE_MASK_REGEX, PHONE_NUMBER_FORMAT)
  }
  return phoneNumber
}
